import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/MissionDetail.css';
import { useMission, useMissionNotes, useMissionPricing } from '../hooks/useMissions';
import MissionMapDetail from '../components/MissionMapDetail';

const MissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mission, loading, error } = useMission(id);
  const { notes, addNote } = useMissionNotes(id);
  const { pricing, loading: pricingLoading } = useMissionPricing(id);
  const [activeTab, setActiveTab] = useState('information');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [showMapDetail, setShowMapDetail] = useState(false);

  const tabs = [
    { id: 'information', label: 'Information générale' },
    { id: 'tarification', label: 'Tarification' },
    { id: 'evaluation', label: 'Evaluation' },
    { id: 'note', label: 'Note interne' }
  ];

  if (loading) {
    return (
      <div className="mission-detail-figma">
        <div className="loading">Chargement de la mission...</div>
      </div>
    );
  }

  if (error || !mission) {
    return (
      <div className="mission-detail-figma">
        <div className="error">
          {error || 'Mission non trouvée'}
        </div>
        <button onClick={() => navigate('/missions')}>Retour aux missions</button>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/missions');
  };

  const handleWarning = () => {
    setShowWarningModal(true);
  };

  const handleSendWarning = async () => {
    try {
      if (warningMessage.trim()) {
        // await sendWarning(warningMessage);
        setShowWarningModal(false);
        setWarningMessage('');
        alert('Avertissement envoyé avec succès');
      }
    } catch (error) {
      alert('Erreur lors de l\'envoi de l\'avertissement');
    }
  };

  const handleAddNote = async () => {
    try {
      if (noteContent.trim()) {
        await addNote(noteContent);
        setNoteContent('');
      }
    } catch (error) {
      alert('Erreur lors de l\'ajout de la note');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return (
          <div className="tab-content-figma">
            <div className="mission-card-detail-figma">
              <div className="mission-header-detail-figma">
                <span className="mission-id-large"># Mission {mission.id.substring(0, 8)}</span>
                <span className="status-badge-large terminee">Terminée</span>
              </div>
              
              <div className="mission-client-detail-figma">
                <div className="client-avatar-large">
                  {mission.client?.name?.charAt(0).toUpperCase() || 'B'}
                </div>
                <div className="client-info-detail">
                  <strong>{mission.client?.name || 'Bel for all for good'}</strong>
                  <div className={`payment-badge-large ${
                    mission.payment_status === 'completed' ? 'payer' : 
                    mission.payment_status === 'failed' ? 'echec' :
                    mission.payment_status === 'pending' ? 'en-cours' : 'payer'
                  }`}>
                    {mission.payment_status === 'completed' ? 'Payé' : 
                     mission.payment_status === 'failed' ? 'Échec de paiement' :
                     mission.payment_status === 'pending' ? 'En cours de paiement' : 'Payé'}
                  </div>
                </div>
              </div>

              <div className="mission-services-detail-figma">
                {mission.service?.type ? (
                  <span className={`service-tag ${(mission.service.type || '').toLowerCase().replace(/\s+/g, '-')}`}>
                    {mission.service.type}
                  </span>
                ) : mission.service_type ? (
                  <span className={`service-tag ${(mission.service_type || '').toLowerCase().replace(/\s+/g, '-')}`}>
                    {mission.service_type}
                  </span>
                ) : (
                  <span className="service-tag">Service non défini</span>
                )}
              </div>

              <div className="mission-dates-detail-figma">
                <div className="date-item-detail">
                  <span className="date-icon">📅</span>
                  <span>Création : {new Date(mission.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="date-item-detail">
                  <span className="date-icon">📅</span>
                  <span>Exécution : {new Date(mission.executed_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>

              <div className="mission-rating-detail-figma">
                {[1, 2, 3, 4, 5].map(star => (
                  <span key={star} className={`star-large ${(mission.rating || 0) >= star ? 'filled' : ''}`}>⭐</span>
                ))}
              </div>

              <div className="mission-actions-detail-figma">
                <button className="action-danger-detail">Annuler mission</button>
                <button className="action-secondary-detail">Rembourser</button>
                <button className="action-primary-detail" onClick={() => setShowMapDetail(true)}>Voir détail</button>
              </div>
            </div>
          </div>
        );

      case 'tarification':
        return (
          <div className="tab-content-figma">
            <div className="tarification-content">
              {pricingLoading ? (
                <div className="loading">Chargement de la tarification...</div>
              ) : pricing ? (
                <>
                  <div className="payment-status-header">
                    <span className="payment-check-icon">✅</span>
                    <span className="payment-status-text">Paiement effectué</span>
                  </div>
                  
                  <div className="pricing-details">
                    {pricing.estimated_price > 0 && (
                      <div className="pricing-row">
                        <span className="pricing-label">Tarif estimé :</span>
                        <span className="pricing-value">{pricing.estimated_price.toLocaleString()} CFA</span>
                      </div>
                    )}
                    {pricing.accepted_price > 0 && (
                      <div className="pricing-row">
                        <span className="pricing-label">Tarif accepté :</span>
                        <span className="pricing-value">{pricing.accepted_price.toLocaleString()} CFA</span>
                      </div>
                    )}
                    {pricing.final_price > 0 && (
                      <div className="pricing-row">
                        <span className="pricing-label">Tarif final :</span>
                        <span className="pricing-value">{pricing.final_price.toLocaleString()} CFA</span>
                      </div>
                    )}
                    {pricing.commission_amount > 0 && (
                      <div className="pricing-row">
                        <span className="pricing-label">Commission prélevée ({pricing.commission_rate ? `${(pricing.commission_rate * 100).toFixed(1)}%` : ''}) :</span>
                        <span className="pricing-value">{pricing.commission_amount.toLocaleString()} CFA</span>
                      </div>
                    )}
                    {pricing.prestataire_earnings !== null && (
                      <div className="pricing-row">
                        <span className="pricing-label">Total versé au prestataire :</span>
                        <span className="pricing-value total">{pricing.prestataire_earnings.toLocaleString()} CFA</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="no-data">Aucune information de tarification disponible</div>
              )}
            </div>
          </div>
        );

      case 'evaluation':
        const reviews = mission.reviews || [];
        return (
          <div className="tab-content-figma">
            <div className="evaluation-content">
              {reviews.length > 0 ? (
                reviews.map((review, index) => {
                  const reviewerName = `${review.first_name || ''} ${review.last_name || ''}`.trim() || 'Utilisateur';
                  const reviewerInitials = reviewerName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                  const reviewRating = review.rating || 0;

                  return (
                    <div className="review-card" key={review.id || index}>
                      <div className="reviewer-info">
                        <div className="reviewer-avatar">
                          {review.profile_picture_url ? (
                            <img src={review.profile_picture_url} alt={reviewerName} />
                          ) : (
                            <span className="avatar-initials">{reviewerInitials}</span>
                          )}
                        </div>
                        <div className="reviewer-details">
                          <div className="reviewer-name">{reviewerName}</div>
                          <div className="reviewer-role">{review.reviewer_type === 'client' ? 'Client' : 'Prestataire'}</div>
                        </div>
                        <div className="review-date">
                          {review.created_at ? new Date(review.created_at).toLocaleDateString('fr-FR') : '-'}
                        </div>
                      </div>

                      <div className="review-rating">
                        {[1, 2, 3, 4, 5].map(star => (
                          <span key={star} className={`review-star ${star <= reviewRating ? 'filled' : ''}`}>⭐</span>
                        ))}
                      </div>

                      <div className="review-text">
                        {review.comment || review.review_text || 'Aucun commentaire'}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-reviews">
                  <p>Aucune évaluation pour cette mission</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'note':
        return (
          <div className="tab-content-figma">
            <div className="note-content">
              <div className="note-header">
                <h3>Note interne</h3>
              </div>
              
              <div className="note-editor-toolbar">
                <div className="toolbar-group">
                  <button className="toolbar-btn">↶</button>
                  <button className="toolbar-btn">↷</button>
                </div>
                <div className="toolbar-group">
                  <button className="toolbar-btn">🖨️</button>
                  <button className="toolbar-btn">📄</button>
                  <select className="toolbar-select">
                    <option>100%</option>
                  </select>
                  <select className="toolbar-select">
                    <option>Paragraph text</option>
                  </select>
                  <select className="toolbar-select">
                    <option>Arial</option>
                  </select>
                  <button className="toolbar-btn">—</button>
                  <span className="toolbar-text">00</span>
                  <button className="toolbar-btn">+</button>
                </div>
              </div>
              
              <div className="note-editor-formatting">
                <div className="formatting-group">
                  <button className="format-btn bold">B</button>
                  <button className="format-btn italic">I</button>
                  <button className="format-btn underline">U</button>
                  <button className="format-btn strikethrough">S</button>
                  <div className="color-picker">
                    <div className="color-square"></div>
                  </div>
                  <button className="format-btn">A</button>
                </div>
                <div className="formatting-group">
                  <button className="format-btn">🔗</button>
                  <button className="format-btn">🖼️</button>
                </div>
              </div>
              
              <div className="note-editor-lists">
                <div className="list-group">
                  <button className="list-btn">• ▼</button>
                  <button className="list-btn">1. ▼</button>
                  <button className="list-btn">✓ ▼</button>
                </div>
                <div className="align-group">
                  <button className="align-btn">⎈</button>
                  <button className="align-btn">⎈</button>
                  <button className="align-btn">⎈</button>
                  <button className="align-btn">⎈</button>
                </div>
                <div className="indent-group">
                  <button className="indent-btn">→</button>
                  <button className="indent-btn">←</button>
                  <button className="indent-btn">⎈</button>
                  <button className="indent-btn">⎈</button>
                </div>
              </div>
              
              <div className="note-editor-area">
                <textarea 
                  className="note-textarea"
                  placeholder="Tapez votre note interne ici..."
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                />
                <div className="note-counter">0 mots</div>
              </div>
              
              <button className="note-save-btn" onClick={handleAddNote}>
                Enregistrer
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mission-detail-figma">
      {/* Header */}
      <div className="mission-detail-header">
        <div className="header-left">
          <button className="back-btn" onClick={handleBack}>
            ←
          </button>
          <h1 className="page-title">Fiche détails missions</h1>
        </div>
        <div className="header-actions">
          <button className="header-btn secondary">Consulter signalement</button>
          <button className="header-btn danger" onClick={handleWarning}>Annuler mission</button>
          <button className="header-btn neutral">Avertir</button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="mission-detail-layout">
        {/* Sidebar Navigation */}
        <div className="mission-sidebar">
          {tabs.map(tab => (
            <button 
              key={tab.id}
              className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="mission-main-content">
          {renderTabContent()}
        </div>
      </div>

      {/* Warning Modal */}
      {showWarningModal && (
        <div className="modal-overlay">
          <div className="warning-modal">
            <div className="modal-header">
              <h3>Avertissement client</h3>
              <button className="modal-close" onClick={() => setShowWarningModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <label htmlFor="warning-message">Message d'avertissement</label>
              <textarea
                id="warning-message"
                className="warning-textarea"
                placeholder="Tapez ici..."
                value={warningMessage}
                onChange={(e) => setWarningMessage(e.target.value)}
              />
            </div>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setShowWarningModal(false)}>
                Annuler
              </button>
              <button className="modal-btn send" onClick={handleSendWarning}>
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}

      {showMapDetail && mission && (
        <MissionMapDetail
          mission={mission}
          onClose={() => setShowMapDetail(false)}
        />
      )}
    </div>
  );
};

export default MissionDetail;